import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, Divider, IconButton, Stack, Typography } from "@mui/material";
import { getInstallmentStatusChipColors, getInstallmentStatusLabel } from "../../../agreements/constants/installmentStatusOptions";
import { getPaymentMethodLabel } from "../../../creditRecovery/constants/paymentMethodOptions";
import { globalMaskDocument } from "../../../../shared/utils";
import { formatDateFnsRemoveTimezone } from "../../../../shared/utils/format-date";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";
import { PAYMENT_METHOD_ICONS } from "../../constants/paymentMethodIcons";
import { ICollectionControlRow } from "../../hooks/useCollectionControlInstallments";

interface Props {
	open: boolean;
	row: ICollectionControlRow | null;
	onClose: () => void;
}

const sectionTitleSx = { mb: 1, textTransform: "uppercase" as const, fontSize: "0.7rem", letterSpacing: 0.5 };

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
	<Box display="flex" justifyContent="space-between" alignItems="center" py={0.4}>
		<Typography variant="body2" color="text.secondary">
			{label}
		</Typography>
		{children}
	</Box>
);

export const ModalCollectionInstallmentDetails = ({ open, row, onClose }: Props) => {
	if (!row) return null;

	const { installment } = row;
	const statusColors = getInstallmentStatusChipColors(installment.status);
	const paymentIcon = PAYMENT_METHOD_ICONS[row.paymentMethod] ?? PAYMENT_METHOD_ICONS.boleto;
	const transferRegistration = installment.agencyTransferRegistration;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
				<Box>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Detalhes da Parcela
					</Typography>
					<Typography variant="caption" color="text.secondary">
						Parcela {installment.number} de {row.installmentsCount}
					</Typography>
				</Box>
				<IconButton onClick={onClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				<Stack spacing={2.5}>
					<Box>
						<Typography variant="subtitle2" color="primary" fontWeight={700} sx={sectionTitleSx}>
							Locatário
						</Typography>
						<InfoRow label="Certificado">
							<Typography variant="body2" fontWeight={500}>
								{row.certificateNumber || "-"}
							</Typography>
						</InfoRow>
						<InfoRow label="Nome">
							<Typography variant="body2" fontWeight={500}>
								{row.proposal.name}
							</Typography>
						</InfoRow>
						<InfoRow label="CPF/CNPJ">
							<Typography variant="body2" fontWeight={500}>
								{globalMaskDocument(row.proposal.document)}
							</Typography>
						</InfoRow>
						<InfoRow label="Imobiliária">
							<Typography variant="body2" fontWeight={500}>
								{row.client?.name || "-"}
							</Typography>
						</InfoRow>
					</Box>

					<Divider />

					<Box>
						<Typography variant="subtitle2" color="primary" fontWeight={700} sx={sectionTitleSx}>
							Parcela
						</Typography>
						<InfoRow label="Valor">
							<Typography variant="body2" fontWeight={600}>
								{formatScaleValueBR(String(installment.value))}
							</Typography>
						</InfoRow>
						<InfoRow label="Vencimento">
							<Typography variant="body2" fontWeight={500}>
								{formatDateFnsRemoveTimezone(installment.dueDate) || "-"}
							</Typography>
						</InfoRow>
						<InfoRow label="Forma de pagamento">
							<Stack direction="row" spacing={0.6} alignItems="center">
								{paymentIcon}
								<Typography variant="body2" fontWeight={500}>
									{getPaymentMethodLabel(row.paymentMethod)}
								</Typography>
							</Stack>
						</InfoRow>
						<InfoRow label="Status">
							<Chip
								size="small"
								label={getInstallmentStatusLabel(installment.status)}
								sx={{ bgcolor: statusColors.bg, color: statusColors.color, fontWeight: 500 }}
							/>
						</InfoRow>
						{installment.status === "paid" && (
							<InfoRow label="Data de pagamento">
								<Typography variant="body2" fontWeight={500}>
									{formatDateFnsRemoveTimezone(installment.paidAt ?? "") || "-"}
								</Typography>
							</InfoRow>
						)}
					</Box>

					<Divider />

					<Box>
						<Typography variant="subtitle2" color="primary" fontWeight={700} sx={sectionTitleSx}>
							Repasse à Imobiliária
						</Typography>
						{transferRegistration ? (
							<>
								<InfoRow label="Valor repassado">
									<Typography variant="body2" fontWeight={600}>
										{formatScaleValueBR(String(transferRegistration.value))}
									</Typography>
								</InfoRow>
								<InfoRow label="Data do repasse">
									<Typography variant="body2" fontWeight={500}>
										{formatDateFnsRemoveTimezone(transferRegistration.transferDate) || "-"}
									</Typography>
								</InfoRow>
								<InfoRow label="Registrado por">
									<Typography variant="body2" fontWeight={500}>
										{transferRegistration.registeredByUserName}
									</Typography>
								</InfoRow>
								{transferRegistration.note && (
									<Box mt={0.5}>
										<Typography variant="caption" color="text.secondary">
											Observação
										</Typography>
										<Typography variant="body2">{transferRegistration.note}</Typography>
									</Box>
								)}
							</>
						) : (
							<Typography variant="body2" color="text.secondary" fontStyle="italic">
								Não informado
							</Typography>
						)}
					</Box>

					<Divider />

					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="body2" color="text.secondary">
							Acordo
						</Typography>
						<Button
							size="small"
							endIcon={<OpenInNewIcon fontSize="small" />}
							onClick={() => window.open(`/sinistros/acordos/detalhe/${row.agreementId}`, "_blank", "noopener")}
						>
							Ver acordo
						</Button>
					</Box>
				</Stack>
			</DialogContent>
		</Dialog>
	);
};
